import React, { createContext, useContext, useEffect, useState } from "react";

interface LateNightContextType {
  isLateNight: boolean;
  lateNightEnabled: boolean;
  toggleLateNight: () => void;
}

const LateNightContext = createContext<LateNightContextType | undefined>(undefined);

export function LateNightProvider({ children }: { children: React.ReactNode }) {
  const [isLateNight, setIsLateNight] = useState(false);
  const [lateNightEnabled, setLateNightEnabled] = useState(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem("lateNightEnabled");
      if (stored !== null) return stored === "true";
    }
    const hours = new Date().getHours();
    return hours >= 22 || hours < 5;
  });

  useEffect(() => {
    const checkTime = () => {
      const now = new Date();
      const hours = now.getHours();
      // 10 PM to 5 AM
      const late = hours >= 22 || hours < 5;
      setIsLateNight(late);
    };

    checkTime();
    const timer = setInterval(checkTime, 60000); // Check every minute

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (lateNightEnabled) {
      document.documentElement.classList.add("dark");
      document.body.style.backgroundColor = "#020617"; // Slate 950 for deep night
    } else {
      document.documentElement.classList.remove("dark");
      document.body.style.backgroundColor = "";
    }
    localStorage.setItem("lateNightEnabled", lateNightEnabled.toString());
  }, [lateNightEnabled]);

  const toggleLateNight = () => {
    setLateNightEnabled(!lateNightEnabled);
  };

  return (
    <LateNightContext.Provider value={{ isLateNight, lateNightEnabled, toggleLateNight }}>
      {children}
    </LateNightContext.Provider>
  );
}

export function useLateNight() {
  const context = useContext(LateNightContext);
  if (context === undefined) {
    throw new Error("useLateNight must be used within a LateNightProvider");
  }
  return context;
}
